export type OfficialFeeStatus =
  | "pending_approval"
  // Wstrzymana przez składającego przed akceptacją — widzi ją wyłącznie on i czeka
  // ona na jego przywrócenie (wtedy wraca do "pending_approval").
  | "on_hold"
  | "approved"
  | "rejected"
  | "payment_uploaded"
  | "acknowledged"
  | "withdrawn";

export interface OfficialFeeRequestList {
  id: string;
  project: string;
  project_name: string;
  project_short_name: string;
  order: string | null;
  order_number: string | null;
  requested_by: string;
  requested_by_name: string;
  paying_company: string | null;
  paying_company_short_code: string | null;
  paying_company_name: string | null;
  office_name: string;
  transfer_name: string;
  amount: string;
  description: string;
  investor_name_required_in_title: boolean;
  is_power_of_attorney: boolean;
  status: OfficialFeeStatus;
  created_at: string;
  updated_at: string;
  payment_confirmation_files: ManagedFileBrief[];
  request_attachment_files: ManagedFileBrief[];
  // Lustro CanApproveFeeForProject z backendu: czy zalogowany użytkownik może
  // decydować (akceptować/odrzucać) o tej opłacie — koordynator tylko dla
  // opłat ze swoich projektów.
  can_decide: boolean;
}

export interface ManagedFileBrief {
  id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  url: string | null;
}

// Zamówienie z DLC_01 (wypisy / mapa zasadnicza / MPZP / wniosek), z którego opłata
// została złożona. Puste dla opłat wpisanych ręcznie w /official-fees.
export interface OfficialFeeSourceLink {
  kind: "wypis" | "mz" | "mpzp" | "wniosek";
  title: string;
  number: string;
  url: string;
  status_display: string;
  is_poa_fee: boolean;
}

export interface OfficialFeeRequestDetail extends OfficialFeeRequestList {
  office_account_number: string;
  office_address: string;
  description: string;
  rejection_comment: string;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  paid_by: string | null;
  paid_by_name: string | null;
  paid_at: string | null;
  acknowledged_at: string | null;
  withdrawn_by: string | null;
  withdrawn_by_name: string | null;
  withdrawn_at: string | null;
  held_by: string | null;
  held_by_name: string | null;
  held_at: string | null;
  source_links: OfficialFeeSourceLink[];
  /** The project has a ZE profile (DLC_01) — its name links to /power-projects/:id.
   *  Always false for contractors. */
  has_ze_project: boolean;
}

export interface OfficialFeeRequestFormData {
  // Opcjonalne wyłącznie dla podwykonawcy: nie widzi przedsięwzięcia ani spółki GK
  // swojego zamówienia, więc oba pola wyprowadza backend ze wskazanego zamówienia.
  // Od użytkownika GK serializer nadal ich wymaga.
  project?: string;
  order?: string;
  paying_company?: string;
  office_name: string;
  office_account_number: string;
  office_address: string;
  transfer_name: string;
  amount: string;
  description?: string;
  investor_name_required_in_title: boolean;
  is_power_of_attorney: boolean;
}

export interface SimilarOfficialFee {
  id: string;
  status: OfficialFeeStatus;
  created_at: string;
  project_name: string;
  project_short_name: string;
  amount: string;
  description: string;
  payment_confirmation_files: ManagedFileBrief[];
}

export interface OfficialFeeHistoryEntry {
  id: string;
  timestamp: string;
  actor_name: string;
  verb: string;
  description: string;
}

export const OFFICIAL_FEE_STATUS_LABELS: Record<OfficialFeeStatus, string> = {
  pending_approval: "Oczekuje na akceptację",
  on_hold: "Wstrzymana",
  approved: "Zaakceptowane",
  rejected: "Odrzucone",
  payment_uploaded: "Potwierdzenie przesłane",
  acknowledged: "Odbiór potwierdzony",
  withdrawn: "Wycofane",
};

/**
 * Statusy w porządku obiegu — bliźniak `_FEE_STATUS_ORDER` z backendu (views.py), po
 * którym sortuje się kolumna „Status". Filtr statusów wypisuje je dokładnie w tej
 * kolejności, żeby lista do zaznaczania czytała się jak ścieżka prośby, nie jak
 * przypadkowy zbiór.
 */
export const OFFICIAL_FEE_STATUS_ORDER: OfficialFeeStatus[] = [
  "pending_approval",
  "on_hold",
  "approved",
  "payment_uploaded",
  "rejected",
  "acknowledged",
  "withdrawn",
];

export const OFFICIAL_FEE_STATUS_COLORS: Record<
  OfficialFeeStatus,
  "yellow" | "green" | "red" | "blue" | "gray" | "orange"
> = {
  pending_approval: "yellow",
  // Nie szary jak „wycofane": wstrzymana wróci do obiegu, gdy autor ją przywróci.
  on_hold: "orange",
  approved: "blue",
  rejected: "red",
  payment_uploaded: "yellow",
  acknowledged: "green",
  withdrawn: "gray",
};

export const OFFICIAL_FEE_HISTORY_VERB_LABELS: Record<string, string> = {
  OFFICIAL_FEE_CREATED: "Złożono zgłoszenie",
  OFFICIAL_FEE_AUTO_APPROVED: "Zaakceptowano automatycznie",
  OFFICIAL_FEE_APPROVED: "Zaakceptowano",
  OFFICIAL_FEE_REJECTED: "Odrzucono",
  OFFICIAL_FEE_PAYMENT_UPLOADED: "Przesłano potwierdzenie",
  OFFICIAL_FEE_ACKNOWLEDGED: "Potwierdzono odbiór",
  OFFICIAL_FEE_DELETED: "Usunięto",
  OFFICIAL_FEE_WITHDRAWN: "Wycofano",
  OFFICIAL_FEE_HELD: "Wstrzymano",
  OFFICIAL_FEE_RESTORED: "Przywrócono do akceptacji",
  OFFICIAL_FEE_ATTACHMENT_ADDED: "Dodano załącznik",
  OFFICIAL_FEE_ATTACHMENT_REMOVED: "Usunięto załącznik",
};

/**
 * Jeden miesiąc w tabeli sum pod listą opłat — wiersz z `/official-fee-requests/summary/`
 * (`?group_by=created_at:month`). `period` to pierwszy dzień miesiąca (ISO), `amount` —
 * suma kwot oddana jako tekst, tak jak robi to agregacja (bez straty na groszach).
 */
export interface OfficialFeeMonthlyTotal {
  period: string;
  count: number;
  amount: string;
}
